export const DEFAULT_PUBLIC_COLORS = {
	primary: "#005fed",
	background: "#f6f7fb",
	card: "#ffffff",
	text: "#162949",
	text_muted: "#6a768b",
	border: "#ebebef",
}

export const COLOR_KEYS = Object.keys(DEFAULT_PUBLIC_COLORS)

export const SUPPORT_LIMITS = { title: 60, hours: 80, phone: 30, note: 160 }

export const SUPPORT_KEYS = Object.keys(SUPPORT_LIMITS)

export const LOGO_MAX_BYTES = 2 * 1024 * 1024

export const LOGO_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"]

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Checks a #RGB or #RRGGBB color.
 *
 * @param {string} value - color text
 *
 * @returns {boolean} true when the value is a valid hex color
 * */
export const isHexColor = (value) => HEX_COLOR.test(String(value ?? "").trim())

/**
 * Expands a #RGB color to #RRGGBB, the only form the native color input accepts.
 *
 * @param {string} value - valid hex color
 *
 * @returns {string} lowercase #rrggbb
 * */
export const toLongHex = (value) => {
	const hex = String(value).trim().toLowerCase()
	return hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex
}

/**
 * Merges the stored colors over the defaults.
 *
 * @param {Object|null} stored - public_colors from the API
 *
 * @returns {Object} the six colors, defaults where nothing is stored
 * */
export const resolveColors = (stored) => {
	const colors = { ...DEFAULT_PUBLIC_COLORS }
	for (const key of COLOR_KEYS) {
		if (stored && isHexColor(stored[key])) colors[key] = stored[key]
	}
	return colors
}

/**
 * Checks a logo file before upload.
 *
 * @param {{ type?: string, size?: number }} file - selected file
 *
 * @returns {"TYPE"|"SIZE"|null} the problem found, or null when acceptable
 * */
export const logoProblem = (file) => {
	if (!file || !LOGO_MIME_TYPES.includes(String(file.type).toLowerCase())) return "TYPE"
	if (!file.size || file.size > LOGO_MAX_BYTES) return "SIZE"
	return null
}

/**
 * Builds the PATCH body from the form, or the invalid fields.
 *
 * @param {{ colors: Object, email: string, support?: Object, poweredBy: string }} form - form state
 *
 * @returns {{ ok: true, payload: Object } | { ok: false, errors: string[] }} the payload, or the names of invalid fields
 * */
export const buildBrandingPayload = (form) => {
	const errors = COLOR_KEYS.filter((key) => !isHexColor(form.colors[key]))
	const email = String(form.email ?? "").trim()
	if (email && !EMAIL.test(email)) errors.push("email")
	const support = Object.fromEntries(SUPPORT_KEYS.map((key) => [key, String(form.support?.[key] ?? "").trim()]))
	for (const key of SUPPORT_KEYS) {
		if (support[key].length > SUPPORT_LIMITS[key]) errors.push(`support_${key}`)
	}
	if (errors.length) return { ok: false, errors }

	const colors = Object.fromEntries(COLOR_KEYS.map((key) => [key, form.colors[key].trim()]))
	const isDefault = COLOR_KEYS.every((key) => colors[key].toLowerCase() === DEFAULT_PUBLIC_COLORS[key])

	return {
		ok: true,
		payload: {
			public_colors: isDefault ? null : colors,
			public_contact_email: email || null,
			...Object.fromEntries(SUPPORT_KEYS.map((key) => [`public_support_${key}`, support[key] || null])),
			public_powered_by: form.poweredBy === "POWERGAPP" ? "POWERGAPP" : "EVPOS",
		},
	}
}

/**
 * Points a stored local upload at the API the backoffice is talking to, so the preview loads even when the
 * server builds its absolute links from a different public URL (local development against a remote STRAPI_URL).
 *
 * @param {string|null} logoUrl - absolute logo URL from the API
 * @param {string} apiBaseUrl - base URL of the backoffice API
 *
 * @returns {string|null} the URL to preview, or null when there is no logo
 * */
export const previewLogoUrl = (logoUrl, apiBaseUrl) => {
	if (!logoUrl) return null
	try {
		const { pathname, search } = new URL(logoUrl)
		return pathname.startsWith("/uploads/") && apiBaseUrl ? `${apiBaseUrl.replace(/\/+$/, "")}${pathname}${search}` : logoUrl
	} catch {
		return logoUrl
	}
}
